import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { memo } from 'react';
import {
    columnSizingOptions,
    useCustomerUpdatesTableStyles,
} from './CustomerUpdatesTable.styles';
import {
    Table,
    TableBody,
    TableCell,
    TableColumnId,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TableRowId,
    Text,
    useTableColumnSizing_unstable,
    useTableFeatures,
    useTableSort,
} from '@fluentui/react-components';
import { ISorting } from '../../../model';
import { CaretDownFilled } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { formatFlightTeamName } from '../../../utils/formatters';
import { getBaseUrl } from '../../../configVariables';
import { dialog } from '@microsoft/teams-js';
import { useSelector } from '../../../store/hooks';
import { selectConfig } from '../../../store/features/config/configSlice';
import {
    CustomerUpdatesTableColumnId,
    getCustomerUpdatesTableColumnsConfig,
} from './CustomerUpdatesTable.utils';
import {
    CustomerFlightTeam,
    CustomerWithStatuses,
    unknownCabinSymbol,
} from '../../../model/customerWithUpdates';
import { CustomerUpdatesType } from '../../../store/features/customerUpdates/customerUpdates.types';

export interface CustomerUpdatesTableProps {
    isFlightTeamBoard: boolean;
    items: readonly CustomerWithStatuses[];
    sorting: ISorting;
    onSortingChange: (sorting: ISorting) => void;
    onPassengerSelect?: (
        customerInfo: CustomerWithStatuses,
        updatesType: CustomerUpdatesType
    ) => void;
}

const flightTeamBoardColumns: readonly CustomerUpdatesTableColumnId[] = [
    'displayName',
    'seatNumber',
    'loyaltyLevel',
    'actions',
] as const;

interface IRowData {
    appearance: 'none';
    item: CustomerWithStatuses;
    rowId: TableRowId;
}

interface IColumnDef {
    columnId: TableColumnId;
    renderCell: (item: CustomerWithStatuses) => React.ReactNode;
    renderHeaderCell: (data?: unknown) => React.ReactNode;
    compare: (a: CustomerWithStatuses, b: CustomerWithStatuses) => number;
}

const CustomerUpdatesTableSection = memo(
    ({
        sectionName,
        headerColSpan,
        rows,
        columns,
        columnSizing,
    }: {
        sectionName: string;
        headerColSpan: number;
        rows: IRowData[];
        columns: IColumnDef[];
        columnSizing: any;
    }) => {
        const styles = useCustomerUpdatesTableStyles();
        const [expanded, setExpanded] = useState(true);
        const caretDownFilledIconRef = useRef<SVGSVGElement>(null);

        const onExpandCallBack = useCallback(() => {
            setExpanded((prev) => !prev);
        }, []);

        useEffect(() => {
            if (caretDownFilledIconRef.current)
                caretDownFilledIconRef.current.style.transform = expanded
                    ? 'rotate(0deg)'
                    : 'rotate(-90deg)';
        }, [expanded]);

        return (
            <>
                <TableRow>
                    <TableCell
                        className={styles.cabinRow}
                        colSpan={headerColSpan}
                        onClick={onExpandCallBack}
                    >
                        <span className={styles.sectionTitle}>
                            <CaretDownFilled
                                fontSize={16}
                                ref={caretDownFilledIconRef}
                            />
                            <Text size={200} weight="semibold">
                                Cabin:
                            </Text>
                            <Text size={400} weight="bold">
                                {sectionName}
                            </Text>
                        </span>
                    </TableCell>
                </TableRow>
                {rows.map(({ item, appearance, rowId }, index) => (
                    <TableRow
                        key={rowId}
                        appearance={appearance}
                        style={{ display: expanded ? 'flex' : 'none' }}
                    >
                        {columns.map((column) => (
                            <TableCell
                                key={`${item.customer.id}-${column.columnId}`}
                                tabIndex={0}
                                role="gridcell"
                                style={{
                                    ...columnSizing.getTableCellProps(
                                        column.columnId
                                    )?.style,
                                    userSelect: 'none',
                                    maxWidth: 'none',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {column.renderCell(item)}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </>
        );
    }
);

const CustomerUpdatesTableRows = memo(
    ({
        rows,
        columns,
        columnSizing,
    }: {
        rows: IRowData[];
        columns: IColumnDef[];
        columnSizing: any;
    }) => {
        const styles = useCustomerUpdatesTableStyles();

        return (
            <>
                {rows.map(({ item, appearance, rowId }) => (
                    <TableRow
                        className={styles.tableRow}
                        key={rowId}
                        appearance={appearance}
                    >
                        {columns.map((column) => (
                            <TableCell
                                key={column.columnId}
                                tabIndex={0}
                                role="gridcell"
                                style={{
                                    ...columnSizing.getTableCellProps(
                                        column.columnId
                                    )?.style,
                                    userSelect: 'none',
                                    maxWidth: 'none',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {column.renderCell(item)}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </>
        );
    }
);

export const CustomerUpdatesTable = memo(
    ({
        isFlightTeamBoard,
        items,
        sorting,
        onSortingChange,
        onPassengerSelect,
    }: CustomerUpdatesTableProps) => {
        const { t } = useTranslation();
        const styles = useCustomerUpdatesTableStyles();
        const { botId } = useSelector(selectConfig);

        const openHelpDialog = useCallback(
            (item: CustomerFlightTeam) => {
                const baseUrl = getBaseUrl();

                dialog.url.bot.open({
                    size: {
                        width: 500,
                        height: 320,
                    },
                    title: formatFlightTeamName(item),
                    url: `${baseUrl}/help`,
                    completionBotId: botId,
                });
            },
            [botId]
        );

        const opendSendMessageToTeamDialog = useCallback(
            (item: CustomerFlightTeam) => {
                const baseUrl = getBaseUrl();

                dialog.url.bot.open({
                    size: {
                        width: 500,
                        height: 250,
                    },
                    title: formatFlightTeamName(item),
                    url: `${baseUrl}/sendMessageToTeam/${item.id}`,
                    completionBotId: botId,
                });
            },
            [botId]
        );

        const columns = useMemo(() => {
            const columns = getCustomerUpdatesTableColumnsConfig(
                t,
                onPassengerSelect ?? (() => {}),
                isFlightTeamBoard
                    ? openHelpDialog
                    : opendSendMessageToTeamDialog
            );

            return isFlightTeamBoard
                ? columns.filter((column) =>
                      flightTeamBoardColumns.includes(
                          column.columnId.toString() as CustomerUpdatesTableColumnId
                      )
                  )
                : columns;
        }, [
            isFlightTeamBoard,
            onPassengerSelect,
            openHelpDialog,
            opendSendMessageToTeamDialog,
            t,
        ]);

        const {
            getRows,
            sort: { getSortDirection },
            columnSizing_unstable: columnSizing,
        } = useTableFeatures(
            {
                getRowId: (row) => row.customer.id,
                columns,
                items: useMemo(() => [...items], [items]), // The hook expects a mutable array, just copy the readonly prop.
            },
            [
                useTableSort({
                    sortState: {
                        sortColumn: sorting.sortBy,
                        sortDirection: sorting.sortByDescending
                            ? 'descending'
                            : 'ascending',
                    },
                }),
                useTableColumnSizing_unstable({
                    columnSizingOptions,
                }),
            ]
        );

        const headerSortProps = useCallback(
            (columnId: TableColumnId) => ({
                onClick: () => {
                    if (columnId.toString().includes('action')) {
                        return;
                    }
                    onSortingChange({
                        ...sorting,
                        sortBy: columnId.toString(),
                    });
                },
                sortDirection: columnId.toString().includes('action')
                    ? undefined
                    : getSortDirection(columnId),
            }),
            [getSortDirection, onSortingChange, sorting]
        );

        const rows = getRows((row) => {
            return {
                ...row,
                appearance: 'none' as const,
            };
        });

        const groupedByCabinDataRows = isFlightTeamBoard
            ? groupRowsByCabin(rows)
            : undefined;

        return (
            <Table
                className={styles.table}
                size="small"
                role="grid"
                aria-label="FlightBaggageStatusTable"
                noNativeElements
                sortable
            >
                <TableHeader>
                    <TableRow className={styles.tableRow}>
                        {columns.map((column) => (
                            <TableHeaderCell
                                key={column.columnId}
                                className={
                                    column.columnId
                                        .toString()
                                        .endsWith('action')
                                        ? styles.actionCell
                                        : ''
                                }
                                style={{
                                    ...columnSizing.getTableHeaderCellProps(
                                        column.columnId
                                    )?.style,
                                    userSelect: 'none',
                                    maxWidth: 'none',
                                }}
                                {...headerSortProps(column.columnId)}
                            >
                                {column.renderHeaderCell()}
                            </TableHeaderCell>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isFlightTeamBoard ? (
                        groupedByCabinDataRows &&
                        groupedByCabinDataRows.map(([cabin, rows]) => (
                            <CustomerUpdatesTableSection
                                key={cabin}
                                sectionName={cabin}
                                rows={rows}
                                headerColSpan={columns.length}
                                columns={columns}
                                columnSizing={columnSizing}
                            />
                        ))
                    ) : (
                        <CustomerUpdatesTableRows
                            rows={rows}
                            columns={columns}
                            columnSizing={columnSizing}
                        />
                    )}
                </TableBody>
            </Table>
        );
    }
);

export default CustomerUpdatesTable;

function groupRowsByCabin(
    rows: IRowData[],
    sortDirection: 'ascending' | 'descending' = 'ascending'
): [string, typeof rows][] {
    const groupedRows = rows.reduce(
        (acc, row) => {
            const cabin = row.item.customer.cabin || unknownCabinSymbol;
            if (!acc[cabin]) {
                acc[cabin] = [];
            }
            acc[cabin].push(row);
            return acc;
        },
        {} as Record<string, typeof rows>
    );

    if (sortDirection === 'descending') {
        return Object.entries(groupedRows).sort(([a], [b]) =>
            a > b ? -1 : a < b ? 1 : 0
        );
    }

    return Object.entries(groupedRows).sort(([a], [b]) =>
        a < b ? -1 : a > b ? 1 : 0
    );
}
